import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { AssetId } from "@visioni/a3s-schema";
import { assetLocationQuery } from "@visioni/a3s-traffic-flow/app";
import type { AssetLocation } from "@visioni/a3s-traffic-flow/client-messages";
import { useTenantId } from "@src/features/tenants";

// Location rarely moves (a commsboard is bolted to a fixed asset), and the
// enquiry is comparatively expensive, so we hold results a good while and don't
// refetch on focus. The operator refreshing the page is enough to re-enquire.
const STALE_TIME_MS = 5 * 60_000;

export interface AssetLocationResult {
  /** Full asset id (as it appears in scenario data), e.g. `A2-G2-CAM_11287`. */
  assetId: string;
  /** Resolved location, or `null` when the asset couldn't be located. */
  location: AssetLocation | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves live geolocation for a set of assets (all the assets in the selected
 * site). Each asset is a separate query so one asset's failure or slow response
 * doesn't block the others — the map can drop markers as they resolve.
 *
 * `null` is an expected result (`{ location: null }` from the endpoint), not an
 * error: many assets have no recent report with a usable fix. Callers must treat
 * a resolved-but-null asset as "couldn't locate — no marker".
 *
 * The full asset id is sent to the endpoint; the service resolves the bare
 * unique id server-side (the offline mock strips the prefix the same way).
 */
export function useAssetLocations(assetIds: readonly string[]): {
  byAssetId: Record<string, AssetLocationResult>;
  isLoading: boolean;
} {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();

  // Stable, de-duplicated id list so the query set doesn't churn when the caller
  // passes a fresh array with the same contents each render.
  const ids = useMemo(() => Array.from(new Set(assetIds)).sort(), [assetIds]);

  const results = useQueries({
    queries: ids.map((assetId) => ({
      ...assetLocationQuery(apiConfig, { assetId: AssetId.toString(AssetId.fromString(assetId)) }),
      enabled: !!tenantId,
      staleTime: STALE_TIME_MS,
      refetchOnWindowFocus: false,
    })),
  });

  return useMemo(() => {
    const byAssetId: Record<string, AssetLocationResult> = {};
    let anyLoading = false;
    ids.forEach((assetId, i) => {
      const q = results[i];
      const isLoading = q?.isLoading ?? false;
      if (isLoading) anyLoading = true;
      byAssetId[assetId] = {
        assetId,
        location: q?.data?.location ?? null,
        isLoading,
        isError: q?.isError ?? false,
      };
    });
    return { byAssetId, isLoading: anyLoading };
  }, [ids, results]);
}
